import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { AccountsBoard } from '@api/BoardSDK';
import { executeIntegration } from '@api/integration-block.server';
import { requireStaff } from '@generated/server/staff.server';
import { withBoardAuth } from '@generated/server/boardAuth.server';

const GMAIL = 'c70cb228-a141-49d4-a586-364172111780';
const TYPES = ['Hot Prospect', 'Red Hook Ads', 'Village Ads', 'Classifieds', 'House Account', 'Mailing Account'] as const;

/** Every account of the chosen types that has an email address (deduped). */
export const getRecipients = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), types: z.array(z.enum(TYPES)), allButVillage: z.boolean().optional() }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    if (!data.allButVillage && data.types.length === 0) return { recipients: [] as { id: string; name: string; email: string; type: string }[] };
    const board = new AccountsBoard();
    const seen = new Set<string>();
    const out: { id: string; name: string; email: string; type: string }[] = [];
    let cursor: string | null | undefined;
    do {
      const res = cursor
        ? await board.items().withColumns(['email', 'type']).withPagination({ cursor }).execute()
        : data.allButVillage
          ? await board.items().withColumns(['email', 'type']).withPagination({ limit: 100 }).execute()
          : await board.items().withColumns(['email', 'type']).where({ type: [...data.types] }).withPagination({ limit: 100 }).execute();
      for (const i of res.items) {
        const email = i.email?.email?.trim().toLowerCase();
        if (!email || !email.includes('@') || seen.has(email)) continue;
        if (data.allButVillage && ['Village Ads', 'Kitty Lead', 'Mailing Account'].includes(i.type ?? '')) continue;
        seen.add(email);
        out.push({ id: i.id, name: i.name, email, type: i.type ?? '' });
      }
      cursor = res.cursor;
    } while (cursor);
    return { recipients: out };
  }));

/** Sends one individual email per address (recipients never see each other). */
export const sendAnnouncement = createServerFn({ method: 'POST' })
  .validator(z.object({
    pass: z.string().optional(),
    to: z.array(z.string().min(3).max(320)).min(1).max(20),
    subject: z.string().min(1).max(300),
    body: z.string().min(1).max(20000),
  }))
  .handler(async ({ data }) => {
    await requireStaff(data.pass);
    const results: { email: string; ok: boolean }[] = [];
    for (const email of data.to) {
      let ok = false;
      for (let attempt = 0; attempt < 2 && !ok; attempt++) {
        try {
          await executeIntegration(GMAIL, { to: [email], subject: data.subject, body: data.body });
          ok = true;
        } catch (e) {
          const err = e as { code?: string; userMessage?: string; message?: string; cause?: unknown };
          console.error('[announce] send failed', { attempt, code: err.code, userMessage: err.userMessage, message: err.message, cause: err.cause });
          if (attempt === 0) await new Promise((r) => setTimeout(r, 1500));
        }
      }
      results.push({ email, ok });
    }
    return { results };
  });

/** Finds Accounts whose name matches Red Hook Stars nominees, so leaders and runners-up can be emailed too. */
export const findNomineeAccounts = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), names: z.array(z.string().trim().min(3).max(120)).max(120) }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const board = new AccountsBoard();
    const clean = (s: string) => s.toLowerCase().replace(/[’'`]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
    const found: { id: string; name: string; email: string; type: string; nominee: string }[] = [];
    const seen = new Set<string>();
    const unique = [...new Set(data.names)];
    for (let i = 0; i < unique.length; i += 8) {
      const chunk = unique.slice(i, i + 8);
      const results = await Promise.all(chunk.map(async (nominee) => {
        const key = clean(nominee).split(' ').filter((w) => w.length > 2 && !['the', 'and'].includes(w)).slice(0, 2).join(' ');
        if (!key) return [];
        try {
          const res = await board.items().withColumns(['email', 'type']).where({ name: { contains: key.split(' ')[0] ?? key } }).withPagination({ limit: 25 }).execute();
          return res.items.filter((it) => clean(it.name).includes(key) || key.includes(clean(it.name))).map((it) => ({ it, nominee }));
        } catch (e) { console.error('[nominees] lookup failed', nominee, e); return []; }
      }));
      for (const { it, nominee } of results.flat()) {
        const email = it.email?.email?.trim().toLowerCase();
        if (!email || !email.includes('@') || seen.has(email)) continue;
        seen.add(email);
        found.push({ id: it.id, name: it.name, email, type: it.type ?? '', nominee });
      }
    }
    return { found, searched: unique.length };
  }));
